import React, { createContext, useContext, useMemo } from 'react';

import {
  OriginTrustStatusType,
  WidgetMessage,
  WidgetMessageType,
} from '@hospitality-consumer-facing/lsk-reservation/shared';

import { useWidgetCommunication } from '../hooks/use-widget-communication.hook';

type MessageHandler = (data?: unknown) => void;

interface WidgetCommunicationContextValue {
  sendMessage: (message: WidgetMessage) => boolean;
  originTrustStatus: OriginTrustStatusType;
  isStandalone: boolean;
  connectionError: string | null;
  parentOrigin: string | null;
}

const WidgetCommunicationContext = createContext<WidgetCommunicationContextValue | null>(null);

interface WidgetCommunicationProviderProps {
  children: React.ReactNode;
  onUnauthorizedOrigin?: (origin: string) => void;
  handlers?: Partial<Record<WidgetMessageType, MessageHandler>>;
}

export function WidgetCommunicationProvider({
  children,
  onUnauthorizedOrigin,
  handlers,
}: WidgetCommunicationProviderProps) {
  const { sendMessage, parentOrigin, originTrustStatus, isStandalone, lastError } =
    useWidgetCommunication({
      onUnauthorizedOrigin: onUnauthorizedOrigin ?? (() => undefined),
      handlers,
    });

  const value = useMemo(
    () => ({
      sendMessage,
      originTrustStatus,
      isStandalone,
      connectionError: lastError && !parentOrigin ? lastError : null,
      parentOrigin,
    }),
    [sendMessage, originTrustStatus, isStandalone, lastError, parentOrigin],
  );

  return (
    <WidgetCommunicationContext.Provider value={value}>
      {children}
    </WidgetCommunicationContext.Provider>
  );
}

export function useWidgetCommunicationContext(): WidgetCommunicationContextValue {
  const context = useContext(WidgetCommunicationContext);

  if (!context) {
    throw new Error(
      'useWidgetCommunicationContext must be used within a WidgetCommunicationProvider',
    );
  }

  return context;
}
